import { useData } from "@hooks/useData";
import http from "@core/fetch";
import { ID } from "@datassential/platform-ui-lib";
import dayjs from "dayjs";

export interface IMacItem {
    id: ID;
    documentType: ID;
    publicationFamily: string;
    title: string;
    description: string;
    countryCodeStr: string;
    itemsCount: number;
    shareLink: string;
    dateStr: string;
    dateSort: Date;
    downloadLinks: Array<{
        fileExtension: string;
        url: string;
    }>;
    thumbnailUrl: string;
}

type ApiMacItemModel = {
    id: ID;
    dateTime: string
    description: string;
    documentType: ID;
    publicationFamily: string;
    downloadLinks: Array<{
        fileExtension: "ppt" | "jpeg";
        linkToDownload: string;
        title: string;
    }>;
    generateLink: string;
    modifiedDate: string;
    shareLinkString: string;
    nonUS: boolean;
    title: "Canadian new kombucha MAC";
    thumnailUrl: string;
};

function getMacWords(rawDescription: string): string[] {
    const rawList = rawDescription.split('<');

    return rawList.map((elem) => elem.split('|')[0].trim());
}

function getMacDescription(rawDescription: string): string {
    const wordsList = getMacWords(rawDescription);

    if (wordsList.length === 0) {
        return '';
    }

    let result = `includes ${wordsList.slice(0, 2).join(', ')}`;

    if (wordsList.length > 2) {
        result += ` and ${wordsList.length - 2} more`;
    }

    return result;
}

type ApiModel = Array<{
    items: ApiMacItemModel[];
}>;

async function getMacsList(): Promise<IMacItem[]> {
    const response: ApiModel = await http.get('documents', {
        doctype: 'Mac',
        skip: 0,
        take: 1000,
        word: '',
        t: Date.now(),
    });

    try {
        return response[0].items.map((item) => ({
            id: item.id,
            title: item.title,
            description: getMacDescription(item.description),
            countryCodeStr: item.nonUS ? 'CAN' : 'USA',
            itemsCount: getMacWords(item.description).length,
            shareLink: item.shareLinkString,
            downloadLinks: [
                {
                    fileExtension: 'PPT',
                    url: item.downloadLinks[1]?.linkToDownload,
                },
                {
                    fileExtension: 'PNG',
                    url: item.downloadLinks[3]?.linkToDownload,
                },
            ],
            dateStr: dayjs(item.modifiedDate).format('MM/DD/YYYY'),
            dateSort: new Date(item.modifiedDate),
            documentType: item.documentType,
            publicationFamily: item.publicationFamily,
            thumbnailUrl: item.thumnailUrl,
        }));
    }
    catch (e) {
        console.error(e);
        return [];
    }
}

export function useMacsList () {
    return useData([], getMacsList)
}